// src/encounter/lib/visual-acuity.ts
// Visual-acuity model + validation + wire mapping (PROD-1537). Faithful port of Flutter's
// VisualAcuityData (visual_acuity_data.dart): the visual-acuity vital (LOINC 31479-8) carries an
// `extraData` object capturing Distance/Near acuity per eye, plus a correction type.
//
// Wire shape (verbatim Flutter toJson — snake_case): {
//   distance: { left_eye: EyeData, right_eye: EyeData },
//   near:     { left_eye: EyeData, right_eye: EyeData },
//   correction_type: string|null
// }  where EyeData = { sc, cc, ph_sc, ph_cc } and each entry = { numerator, denominator }.
//
// Snellen values: numerator is fixed "20"; denominator ∈ the allowed set (visual_acuity_input_formatter).
// Distance captures Sc/Cc/PhSc/PhCc; Near captures Sc/Cc only (Flutter visual_acuity_form.dart).

export interface NumeratorDenominator {
  numerator?: string | null;
  denominator?: string | null;
}

export interface EyeData {
  sc?: NumeratorDenominator;
  cc?: NumeratorDenominator;
  phSc?: NumeratorDenominator;
  phCc?: NumeratorDenominator;
}

export interface VisualRange {
  leftEye?: EyeData;
  rightEye?: EyeData;
}

export interface VisualAcuityData {
  distance?: VisualRange;
  near?: VisualRange;
  correctionType?: string | null;
}

/** Flutter correctionTypes (single-select chips). */
export const CORRECTION_TYPES = ['No Correction', 'Glasses', 'Contact Lenses'] as const;
export type CorrectionType = (typeof CORRECTION_TYPES)[number];

/** Snellen numerator is fixed at 20 (visual_acuity_input_formatter_numerator). */
export const ALLOWED_NUMERATOR = ['20'] as const;
/** Allowed Snellen denominators (visual_acuity_input_formatter). */
export const ALLOWED_DENOMINATOR = ['10', '15', '20', '25', '30', '40', '50', '60', '70', '80', '100', '200', '400'] as const;

const blank = (s: string | null | undefined) => !s || !s.trim();

export function emptyEye(): EyeData {
  return { sc: {}, cc: {}, phSc: {}, phCc: {} };
}
export function emptyVisualAcuity(): VisualAcuityData {
  return {
    distance: { leftEye: emptyEye(), rightEye: emptyEye() },
    near: { leftEye: emptyEye(), rightEye: emptyEye() },
    correctionType: null,
  };
}

function pairEmpty(p: NumeratorDenominator | undefined): boolean {
  return !p || (blank(p.numerator) && blank(p.denominator));
}

/**
 * A numerator/denominator pair is invalid unless it is fully empty OR both sides are present AND
 * in their allowed sets (Flutter isPairInvalid, visual_acuity_form.dart:62). An incomplete or
 * out-of-range pair is invalid.
 */
export function isPairInvalid(pair: NumeratorDenominator | undefined): boolean {
  if (pairEmpty(pair)) return false;
  const num = pair!.numerator?.trim();
  const den = pair!.denominator?.trim();
  if (!num || !(ALLOWED_NUMERATOR as readonly string[]).includes(num)) return true;
  if (!den || !(ALLOWED_DENOMINATOR as readonly string[]).includes(den)) return true;
  return false;
}

function eyePairs(eye: EyeData | undefined, includePinhole: boolean): (NumeratorDenominator | undefined)[] {
  if (!eye) return [];
  return includePinhole ? [eye.sc, eye.cc, eye.phSc, eye.phCc] : [eye.sc, eye.cc];
}

/**
 * Validate the whole form (mirrors Flutter's per-pair isPairInvalid sweep). Returns an error
 * string when any entered pair is incomplete/out-of-range, else null. Distance includes the
 * pinhole rows (Ph Sc / Ph Cc); Near is Sc / Cc only.
 */
export function validateVisualAcuity(data: VisualAcuityData): string | null {
  const groups: (NumeratorDenominator | undefined)[] = [
    ...eyePairs(data.distance?.leftEye, true),
    ...eyePairs(data.distance?.rightEye, true),
    ...eyePairs(data.near?.leftEye, false),
    ...eyePairs(data.near?.rightEye, false),
  ];
  if (groups.some(isPairInvalid)) {
    return 'Please enter valid values for Visual Acuity (both numerator and denominator must be valid).';
  }
  return null;
}

/** True when nothing has been entered (all pairs empty + no correction type). */
export function isVisualAcuityEmpty(data: VisualAcuityData): boolean {
  const allPairs = [
    ...eyePairs(data.distance?.leftEye, true),
    ...eyePairs(data.distance?.rightEye, true),
    ...eyePairs(data.near?.leftEye, true),
    ...eyePairs(data.near?.rightEye, true),
  ];
  return allPairs.every(pairEmpty) && blank(data.correctionType);
}

// ── Wire mapping (snake_case, verbatim Flutter toJson/fromJson) ───────────────
type PairJson = { numerator?: string | null; denominator?: string | null };
type EyeJson = { sc?: PairJson | null; cc?: PairJson | null; ph_sc?: PairJson | null; ph_cc?: PairJson | null };
type RangeJson = { left_eye?: EyeJson | null; right_eye?: EyeJson | null };
export interface VisualAcuityJson {
  distance?: RangeJson | null;
  near?: RangeJson | null;
  correction_type?: string | null;
}

const pairJson = (p?: NumeratorDenominator): PairJson => ({ numerator: p?.numerator ?? null, denominator: p?.denominator ?? null });
const eyeJson = (e?: EyeData): EyeJson => ({ sc: pairJson(e?.sc), cc: pairJson(e?.cc), ph_sc: pairJson(e?.phSc), ph_cc: pairJson(e?.phCc) });
const rangeJson = (r?: VisualRange): RangeJson => ({ left_eye: eyeJson(r?.leftEye), right_eye: eyeJson(r?.rightEye) });

export function toVisualAcuityJson(data: VisualAcuityData): VisualAcuityJson {
  return { distance: rangeJson(data.distance), near: rangeJson(data.near), correction_type: data.correctionType ?? null };
}

const pairFrom = (p?: PairJson | null): NumeratorDenominator => ({ numerator: p?.numerator ?? null, denominator: p?.denominator ?? null });
const eyeFrom = (e?: EyeJson | null): EyeData => ({ sc: pairFrom(e?.sc), cc: pairFrom(e?.cc), phSc: pairFrom(e?.ph_sc), phCc: pairFrom(e?.ph_cc) });
const rangeFrom = (r?: RangeJson | null): VisualRange => ({ leftEye: eyeFrom(r?.left_eye), rightEye: eyeFrom(r?.right_eye) });

export function fromVisualAcuityJson(json: VisualAcuityJson | null | undefined): VisualAcuityData {
  if (!json) return emptyVisualAcuity();
  return { distance: rangeFrom(json.distance), near: rangeFrom(json.near), correctionType: json.correction_type ?? null };
}
